import React from 'react';
import { motion } from 'framer-motion';
import { Activity, Heart, FlaskConical, Pill, Bed, Eye, Stethoscope, Scissors, HeartPulse, Droplets, TestTube, Scan, MonitorPlay } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import eyeimg from "../assets/eyecheckup.png";
import emergency from "../assets/emergencycare.png";
import icu from "../assets/ICU.png";
import ot from "../assets/OT.png";
import cath from "../assets/cathlab.png";
import xray from "../assets/xray.png";
import ultrasound from "../assets/ultrasound.JPG";
import cardiac from "../assets/cathlab.png";
import kidney from "../assets/dailysis.JPG";
import medicalshop from "../assets/medicalshop.png";
import lab from "../assets/lab.JPG"

const Services = () => {
  const mainServices = [
    {
      title: 'Emergency & Trauma Care',
      description: 'Round-the-clock emergency and trauma care services with a dedicated team of emergency medicine specialists. Our state-of-the-art emergency department is equipped to handle all types of medical emergencies.',
      image: emergency,
      icon: Activity,
      features: ['24/7 Availability', 'Ambulance Service', 'Trauma Unit', 'Critical Care'],
    },
    {
      title: 'Intensive Care Unit (ICU)',
      description: 'Our ICU is equipped with advanced monitoring systems and life support equipment. Staffed by experienced intensivists and trained nurses providing continuous patient monitoring.',
      image: icu,
      icon: Heart,
      features: ['Advanced Monitoring', 'Ventilator Support', '24/7 Nursing', 'Critical Care Specialists'],
    },
    {
      title: 'Laboratory Services',
      description: 'Comprehensive diagnostic laboratory with advanced equipment for accurate and quick test results. From routine blood tests to specialized diagnostic procedures.',
      image: lab,
      icon: FlaskConical,
      features: ['Blood Tests', 'Urine Analysis', 'Pathology', 'Microbiology'],
    },
    {
      title: '24X7 Medical Shop',
      description: 'In-house pharmacy open 24/7 providing all prescribed medications and medical supplies. Convenient and accessible for patients and their families.',
      image: medicalshop,
      icon: Pill,
      features: ['24/7 Availability', 'All Medicines', 'Medical Supplies', 'Prescription Service'],
    },
    {
      title: 'General Surgery',
      description: 'Expert surgical care across various specialties including general surgery, orthopedic surgery, and maxillofacial surgery. Modern operation theaters with advanced equipment.',
      image: ot,
      icon: Scissors,
      features: ['General Surgery', 'Orthopedic Surgery', 'Laparoscopic Surgery', 'Modern OT'],
    },
    {
      title: 'Eye Care (Ophthalmology)',
      description: 'Comprehensive eye care services including consultation, diagnostics, and treatment. Led by experienced ophthalmologists dedicated to preserving and improving vision.',
      image: eyeimg,
      icon: Eye,
      features: ['Eye Exams', 'Cataract Surgery', 'Glaucoma Treatment', 'Vision Correction'],
    },
    {
    title: 'Cardiac Care Centre',
    description: 'A specialized tertiary cardiac care centre, equipped to handle complex cardiac vascular journeys, offering state-of-the-art care and surgical units.',
    image: cardiac,
    icon: HeartPulse, // Assuming you have a suitable icon like HeartPulse
    features: ['Cardiac Surgery Unit', 'Cardiac Intensive Care Unit (CICU)', 'Heart Disease Treatment', 'Cardiac Consultation'],
    },
    {
    title: 'Kidney Care & Dialysis',
    description: 'Comprehensive kidney care services, focused on heart and kidney health (Swasthya heart and kidney care). Includes advanced dialysis and kidney management.',
    image: kidney,
    icon: Droplets, // Assuming a suitable icon like Droplets or Kidney icon
    features: ['Hemodialysis', 'Peritoneal Dialysis', 'Nephrology Consultation', 'Renal Biopsy'],
    },
    {
    title: 'Cath Lab',
    description: 'Our marvelous, cutting-edge cath lab unit is equipped with advanced technology for precise diagnostic and interventional cardiac procedures.',
    image: cath,
    icon: TestTube, // A general science/procedure icon, replace with a specific Cath Lab one if available
    features: ['Diagnostic Angiography', 'Angioplasty and Stenting', 'Pacemaker Implantation', 'Advanced Cardiac Imaging'],
    },
    {
    title: 'X-Ray',
    description: 'High-quality conventional X-ray services for rapid and accurate diagnosis of injuries, fractures, and internal conditions.',
    image: xray,
    icon: Scan, // Assuming a suitable icon like Scan or Radiation icon
    features: ['Chest X-rays', 'Skeletal X-rays', 'Abdominal X-rays', 'Portable X-ray Services'],
    },
    {
    title: 'Ultrasound Imaging',
    description: 'Advanced ultrasound and sonography services for non-invasive imaging of internal organs, pregnancy monitoring, and soft tissues.',
    image: ultrasound,
    icon: MonitorPlay, // Assuming a suitable icon for imaging/screen
    features: ['Abdominal Ultrasound', 'Pelvic Ultrasound', 'Obstetric Sonography', 'Doppler Studies'],
    },
  ];

  const facilities = [
    {
      icon: Bed,
      title: 'Spacious Rooms',
      description: 'Comfortable and well-maintained wards with proper ventilation',
    },
    {
      icon: Stethoscope,
      title: 'Qualified Staff',
      description: 'Highly trained doctors, nurses, and support staff',
    },
    {
      icon: Heart,
      title: 'Cashless Facility',
      description: 'Easy and affordable billing with cashless options',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1516549655169-df83a0774514"
          alt="Services"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-sky-900/90 to-blue-900/70"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-bold text-white mb-4"
          >
            Our <span className="text-sky-300">Services</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-white/90 max-w-2xl mx-auto"
          >
            Comprehensive healthcare services for all your medical needs
          </motion.p>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-gradient-to-b from-white to-sky-50">
        <div className="container mx-auto px-4">
          <div className="space-y-20">
            {mainServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Image Section */}
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="relative group">
                      <div className="absolute -inset-4 bg-gradient-to-r from-sky-400 to-blue-500 rounded-3xl opacity-20 group-hover:opacity-30 transition-opacity blur-xl"></div>
                      <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-96 object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                        <div className="absolute bottom-6 left-6">
                          <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center">
                            <service.icon className="w-8 h-8 text-white" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <motion.div
                      initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                    >
                      <div className="inline-block px-4 py-2 bg-sky-100 rounded-full text-sky-600 font-medium text-sm mb-4">
                        Core Service
                      </div>
                      <h2 className="text-4xl font-bold mb-6 text-gray-800">{service.title}</h2>
                      <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                        {service.description}
                      </p>

                      <div className="grid grid-cols-2 gap-4">
                        {service.features.map((feature, idx) => (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.1 }}
                            className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all"
                          >
                            <div className="w-2 h-2 bg-sky-500 rounded-full"></div>
                            <span className="text-gray-700 font-medium text-sm">{feature}</span>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Facilities */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800">
              Patient <span className="text-sky-500">Facilities</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Additional facilities designed for your comfort and convenience
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {facilities.map((facility, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="border-none shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2 bg-gradient-to-br from-white to-sky-50 h-full">
                  <CardContent className="p-8 text-center">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-sky-400 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg"
                    >
                      <facility.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    <h3 className="text-xl font-bold mb-3 text-gray-800">{facility.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{facility.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-sky-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-sky-500 to-blue-600 rounded-3xl p-12 text-white text-center shadow-2xl"
          >
            <h2 className="text-4xl font-bold mb-4">Need Our Services?</h2>
            <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
              Contact us today to learn more about our services or to schedule an appointment
            </p>
            <a href="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-sky-600 px-8 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all"
              >
                Contact Us Now
              </motion.button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;